import { queryOptions, useQuery } from "@tanstack/react-query";

import {
  CAR_CONDITIONS,
  CAR_STATUSES,
  getConditionsFromCars,
  getDataFilteredAndSorted,
  getStatusesFromCars,
  mapCarPayloadToCar,
} from "./utils";

import { env } from "@/config/env";
import { api } from "@/lib/api-client";
import { hasAccessToken } from "@/lib/auth";

import type { QueryConfig } from "@/lib/react-query";
import type { Car, CarPayload } from "@/types/car";
import type { PaginatedList } from "@/types/entity";
import type { CarConditions, CarStatuses, QueryParams } from "./types";

type GarageQueryParams = QueryParams & {
  garageId?: string;
};

export const getGarageCars = async ({
  date,
  garageId,
  page,
  pageSize,
}: GarageQueryParams): Promise<PaginatedList<Car>> => {
  const query = date ? `?date=${date}` : `?page=${page}&page_size=${pageSize}`;
  const response = (await api.get(
    `/carburate/garages/${garageId}/cars/${query}`
  )) as PaginatedList<CarPayload>;
  return {
    ...response,
    results: response.results.map(mapCarPayloadToCar),
  } as PaginatedList<Car>;
};

export const getGarageCarsQueryOptions = ({
  date,
  garageId,
  page,
  pageSize,
}: GarageQueryParams) => {
  return queryOptions({
    queryKey: ["garage-cars", { date, garageId, page, pageSize }],
    queryFn: () => getGarageCars({ date, garageId, page, pageSize }),
  });
};

type UseGarageCarsOptions = GarageQueryParams & {
  queryConfig?: QueryConfig<typeof getGarageCars>;
};

export const useGarageCars = ({
  date,
  garageId,
  parameters,
  queryConfig,
  page = 1,
  pageSize,
  sort,
}: UseGarageCarsOptions) => {
  const isClientPagination = env.PAGINATION === "client";

  const currentPage = isClientPagination ? 1 : page;
  const currentPageSize = isClientPagination ? 999999 : pageSize;

  let data: PaginatedList<Car>;
  let conditions: CarConditions = CAR_CONDITIONS;
  let statuses: CarStatuses = CAR_STATUSES;

  // If date is provided, we ignore pagination params
  const isAggregatedData = !!date;
  const params = isAggregatedData ? { date } : { page: currentPage, pageSize: currentPageSize };

  const {
    data: response,
    isSuccess,
    ...props
  } = useQuery({
    enabled: hasAccessToken() && !!garageId,
    ...getGarageCarsQueryOptions({ garageId, ...params }),
    refetchOnWindowFocus: true,
    ...queryConfig,
  });

  data = response!;

  if (isClientPagination && isSuccess && !isAggregatedData) {
    conditions = getConditionsFromCars(data.results);
    statuses = getStatusesFromCars(data.results);
    data = getDataFilteredAndSorted(data, { parameters, page, pageSize, sort });
  }

  return { data, conditions, statuses, isSuccess, ...props };
};
